// src/utils/templateComponentServices.js
import {
  saveTemplate,
  updateTemplate,
  fetchTemplate,
  fetchCanvasSizes,
  fetchCanvasColors,
  generateSignedUrls,
  fetchDataBindingValues,
  fetchInternalConfigurations,
} from "./truInkTemplate";
import { exportToJson, importFromJson } from "./canvaUtils";

// Function to handle the saving and updating of a template
export async function saveDesign(
  elements,
  eventName,
  tenantId,
  siteId,
  canvasDimensions,
  currentTemplateId,
  navigate
) {
  try {
    const updatedElements = await processImagesForUpload(elements);

    const templateData = exportToJson(
      updatedElements,
      eventName,
      "store_101",
      tenantId,
      siteId,
      canvasDimensions
    );

    let response;
    if (currentTemplateId) {
      response = await updateTemplate(
        templateData,
        currentTemplateId,
        tenantId,
        siteId
      );
    } else {
      response = await saveTemplate(templateData, tenantId, siteId);
      if (response.id) {
        navigate(`/${response.id}`);
      }
    }
    return response;
  } catch (error) {
    console.error("Error saving template:", error);
    throw error;
  }
}

// Helper function to process and upload images
async function processImagesForUpload(elements) {
  const imagesToUpload = elements.filter(
    (el) => el.type === "image" && el.src && el.src.startsWith("data:")
  );

  if (imagesToUpload.length === 0) {
    return elements;
  }

  const fileInfos = imagesToUpload.map((el) => {
    const mimeType = el.src.split(";")[0].split(":")[1];
    const extension = mimeType.split("/")[1];
    return {
      elementId: el.id,
      fileName: `${el.id}.${extension}`,
      contentType: mimeType,
      dataUrl: el.src,
    };
  });

  const contentTypes = Object.fromEntries(
    fileInfos.map((info) => [info.elementId, info.contentType])
  );
  const fileNames = fileInfos.map((info) => info.fileName);

  const signedUrlsResponse = await generateSignedUrls(fileNames, contentTypes);

  const uploadResults = {};
  await Promise.all(
    fileInfos.map(async (fileInfo) => {
      try {
        const signedUrlInfo = signedUrlsResponse[fileInfo.elementId];
        if (!signedUrlInfo) {
          throw new Error(`No signed URL for element ${fileInfo.elementId}`);
        }
        const response = await fetch(fileInfo.dataUrl);
        const blob = await response.blob();
        const uploadResponse = await fetch(signedUrlInfo.signedUrl, {
          method: "PUT",
          body: blob,
          headers: { "Content-Type": fileInfo.contentType },
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`
          );
        }
        uploadResults[fileInfo.elementId] = signedUrlInfo.publicUrl;
      } catch (error) {
        console.error(`Error uploading ${fileInfo.elementId}:`, error);
        throw error;
      }
    })
  );

  return elements.map((el) => {
    if (el.type === "image" && uploadResults[el.id]) {
      return { ...el, src: uploadResults[el.id] };
    }
    return el;
  });
}

// Function to load template data from various sources
export async function loadInitialData(templateId, tenantId, siteId) {
  if (!templateId || !tenantId) {
    throw new Error("Template ID or Tenant ID not available");
  }

  const [
    templateData,
    screenSizes,
    canvaColors,
    dataBindingValues,
    internalConfigs,
  ] = await Promise.all([
    fetchTemplate(tenantId, templateId, siteId),
    fetchCanvasSizes(tenantId),
    fetchCanvasColors(tenantId),
    fetchDataBindingValues(tenantId),
    fetchInternalConfigurations(),
  ]);

  const elements = templateData.elements ? importFromJson(templateData) : [];
  const eventName = templateData.name || "Template";

  let canvasColor = [];
  if (templateData.canvasColorId && canvaColors && canvaColors.length > 0) {
    const matchingColor = canvaColors.find(
      (color) => color.id === templateData.canvasColorId
    );
    if (matchingColor && matchingColor.values) {
      canvasColor = matchingColor.values;
    } else {
      canvasColor = canvaColors[0]?.values || [];
    }
  } else {
    canvasColor = canvaColors[0]?.values || [];
  }

  let resolution = "1920x1080";
  if (templateData.canvasSizeId && screenSizes && screenSizes.length > 0) {
    const matchingSize = screenSizes.find(
      (size) => size.id === templateData.canvasSizeId
    );
    if (matchingSize && matchingSize.resolution) {
      resolution = matchingSize.resolution;
    } else if (screenSizes[0]?.resolution) {
      resolution = screenSizes[0].resolution;
    }
  }

  // Parse customProperties if it's a string
  let customProperties = {};
  if (templateData.customProperties) {
    if (typeof templateData.customProperties === 'string') {
      try {
        customProperties = JSON.parse(templateData.customProperties);
      } catch (e) {
        console.error("Failed to parse customProperties:", e);
        customProperties = {};
      }
    } else {
      customProperties = templateData.customProperties;
    }
  }

  return { elements, eventName, resolution, canvasColor, dataBindingValues, internalConfigs, truInkEMMSTemplate: customProperties?.truInkEMMSTemplate || false, customProperties };
}
